/**
 * Notification Triggers Hook
 * Provides functions to trigger desktop notifications for various events
 */

import { useAuth } from '@/contexts/AuthContext';
import { desktopNotifications } from '@/services/desktopNotifications';
import { format } from 'date-fns';

export function useNotificationTriggers() {
  const { user } = useAuth();

  const isDesktopNotificationsEnabled = () => {
    const prefs = user?.preferences;
    return prefs?.desktop_notifications_enabled ?? true;
  };

  const isNotificationTypeEnabled = (type: 'workflows' | 'communications' | 'tasks') => {
    const prefs = user?.preferences;
    switch (type) {
      case 'workflows':
        return prefs?.notify_workflows ?? true;
      case 'communications':
        return prefs?.notify_communications ?? true;
      case 'tasks':
        return prefs?.notify_tasks ?? true;
      default:
        return true;
    }
  };

  /**
   * Trigger notification for workflow completion
   */
  const notifyWorkflowCompletion = async (params: {
    workflowName: string;
    memberName: string;
    workflowId?: number;
    memberId?: number;
  }) => {
    if (!isDesktopNotificationsEnabled() || !isNotificationTypeEnabled('workflows')) {
      return null;
    }

    const link = params.workflowId
      ? `/workflows/${params.workflowId}`
      : params.memberId
      ? `/members/${params.memberId}`
      : undefined;

    return desktopNotifications.notifyWorkflowCompletion(
      params.workflowName,
      params.memberName,
      link
    );
  };

  /**
   * Trigger notification for inbound communication
   */
  const notifyInboundCommunication = async (params: {
    from: string;
    preview: string;
    type: 'email' | 'sms' | 'phone';
    conversationId?: number;
    memberId?: number;
  }) => {
    if (!isDesktopNotificationsEnabled() || !isNotificationTypeEnabled('communications')) {
      return null;
    }

    const link = params.conversationId
      ? `/communications/${params.conversationId}`
      : params.memberId
      ? `/members/${params.memberId}`
      : undefined;

    return desktopNotifications.notifyInboundCommunication(
      params.from,
      params.preview,
      params.type,
      link
    );
  };

  /**
   * Trigger notification for task assignment
   */
  const notifyTaskAssignment = async (params: {
    taskTitle: string;
    assignedBy: string;
    dueDate?: Date | string;
    taskId?: number;
    memberId?: number;
  }) => {
    if (!isDesktopNotificationsEnabled() || !isNotificationTypeEnabled('tasks')) {
      return null;
    }

    const dueDateFormatted = params.dueDate
      ? typeof params.dueDate === 'string'
        ? params.dueDate
        : format(params.dueDate, 'MMM d, yyyy')
      : undefined;

    const link = params.taskId
      ? `/tasks/${params.taskId}`
      : params.memberId
      ? `/members/${params.memberId}`
      : undefined;

    return desktopNotifications.notifyTaskAssignment(
      params.taskTitle,
      params.assignedBy,
      dueDateFormatted,
      link
    );
  };

  /**
   * Trigger notification for urgent/high priority items
   */
  const notifyUrgent = async (params: { title: string; message: string; link?: string }) => {
    if (!isDesktopNotificationsEnabled()) {
      return null;
    }

    return desktopNotifications.notifyUrgent(params.title, params.message, params.link);
  };

  /**
   * Trigger notification when a sales activity is created
   */
  const notifyActivityCreated = async (params: {
    activityType: string;
    title: string;
    opportunityId?: string;
  }) => {
    if (!isDesktopNotificationsEnabled() || !isNotificationTypeEnabled('tasks')) {
      return null;
    }

    const link = params.opportunityId
      ? `/platform-sales?opportunity=${params.opportunityId}&tab=activity`
      : '/platform-sales?tab=activities';

    return desktopNotifications.showNotificationWithAction(
      {
        title: `New ${params.activityType}: ${params.title}`,
        body: `${params.activityType.charAt(0).toUpperCase() + params.activityType.slice(1)} scheduled`,
      },
      () => { window.location.href = link; }
    );
  };

  /**
   * Trigger notification when a sales activity is completed
   */
  const notifyActivityCompleted = async (params: {
    activityType: string;
    title: string;
    opportunityId?: string;
  }) => {
    if (!isDesktopNotificationsEnabled() || !isNotificationTypeEnabled('tasks')) {
      return null;
    }

    const link = params.opportunityId
      ? `/platform-sales?opportunity=${params.opportunityId}&tab=activity`
      : '/platform-sales?tab=activities';

    return desktopNotifications.showNotificationWithAction(
      {
        title: `${params.activityType.charAt(0).toUpperCase() + params.activityType.slice(1)} completed`,
        body: `'${params.title}' has been marked complete`,
      },
      () => { window.location.href = link; }
    );
  };

  /**
   * Request notification permission from browser
   */
  const requestPermission = async () => {
    return desktopNotifications.requestPermission();
  };

  /**
   * Check if notifications are supported
   */
  const isSupported = () => {
    return desktopNotifications.isNotificationSupported();
  };

  /**
   * Get current permission state
   */
  const getPermissionState = () => {
    return desktopNotifications.getPermissionState();
  };

  return {
    notifyWorkflowCompletion,
    notifyInboundCommunication,
    notifyTaskAssignment,
    notifyActivityCreated,
    notifyActivityCompleted,
    notifyUrgent,
    requestPermission,
    isSupported,
    getPermissionState,
    isDesktopNotificationsEnabled,
  };
}
